import { ElementRef, Injectable, inject } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import domtoimage from 'dom-to-image';
import { Html2CanvasThumbnailHelper } from '../shared/utils/html2canvas-thumbnail.helper';
import { HeaderUtilityService } from './header-utility.service';
import { LoggerService } from '@optima-stride-ai/core';
import {
  FileExtensions,
  FileExtensionGroups,
  LogMessages,
  MimeTypes
} from '../shared/enums/app-constants';

export interface ThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'png' | 'jpeg';
}

export interface ThumbnailResult {
  dataUrl: string;
  fileSize: number;
  method?: string;
  error?: string;
}

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs';

@Injectable({
  providedIn: 'root'
})
export class ThumbnailManagerService {
  private readonly headerUtilityService = inject(HeaderUtilityService);
  private readonly logger = inject(LoggerService);

  /**
   * Generates a thumbnail for a file using the appropriate method based on file type
   */
  async generateThumbnailForFile(file: File): Promise<string | null> {
    try {
      const fileName = file.name.toLowerCase();
      const fileType = fileName.substring(fileName.lastIndexOf('.'));

      this.logger.debug(`${LogMessages.PROCESS_GENERATING_IMAGE} ${file.name} (${fileType})`);

      // Handle PDF files
      if (fileType === FileExtensions.PDF) {
        return this.generatePdfThumbnailFromFile(file);
      }

      // Handle image files
      if (FileExtensionGroups.IMAGES.includes(fileType as FileExtensions)) {
        return this.generateImageThumbnailFromFile(file);
      }

      // Handle video files
      if (FileExtensionGroups.VIDEOS.includes(fileType as FileExtensions)) {
        return this.generateVideoThumbnailFromFile(file);
      }

      // Handle audio files
      if (FileExtensionGroups.AUDIO.includes(fileType as FileExtensions)) {
        return this.generateAudioThumbnailFromFile(file);
      }

      // Handle markdown files
      if (FileExtensionGroups.MARKDOWN.includes(fileType as FileExtensions)) {
        return this.generateMarkdownThumbnailFromFile(file);
      }

      // Handle Excel files using the inlined client-side renderer
      if (FileExtensionGroups.EXCEL_FILES.includes(fileType as FileExtensions)) {
        const result: ThumbnailResult = await this.generateThumbnail(file);

        if (result.dataUrl) {
          return result.dataUrl;
        } else {
          this.logger.warn(`${LogMessages.WARNING_THUMBNAIL_FAILED}: ${result.error}`);
          return null;
        }
      }

      // Handle other document and archive files with generic thumbnails
      if (FileExtensionGroups.DOCUMENTS.includes(fileType as FileExtensions) || 
          FileExtensionGroups.ARCHIVES.includes(fileType as FileExtensions) ||
          FileExtensionGroups.PRESENTATIONS.includes(fileType as FileExtensions) ||
          FileExtensionGroups.TEXT.includes(fileType as FileExtensions)) {
        return this.generateGenericFileThumbnail(file);
      }

      this.logger.warn(`${LogMessages.WARNING_UNSUPPORTED_FILE}: ${fileType}`);
      return null;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_THUMBNAIL_GENERATION}: ${error}`);
      return null;
    }
  }

  /**
   * Generates a PDF thumbnail from a File object
   */
  private async generatePdfThumbnailFromFile(file: File): Promise<string | null> {
    try {
      this.logger.debug(`${LogMessages.PROCESS_PDF_LOADING}: ${file.name}`);
      
      // Convert File to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load PDF with minimal configuration
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        disableAutoFetch: true,
        disableStream: true
      });
      
      const pdf = await loadingTask.promise;
      this.logger.debug(`${LogMessages.SUCCESS_PDF_LOADED}, pages: ${pdf.numPages}`);

      // Get first page
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.3 }); // thumbnail scale

      // Create canvas
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render page to canvas
      await page.render({ 
        canvasContext: context, 
        viewport,
        canvas: canvas
      }).promise;
      this.logger.debug(LogMessages.SUCCESS_THUMBNAIL_GENERATED);

      // Convert canvas to image
      const thumbnail = canvas.toDataURL(MimeTypes.PNG);
      this.logger.info(LogMessages.SUCCESS_THUMBNAIL_GENERATED);
      return thumbnail;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_PDF_THUMBNAIL}: ${error}`);
      return null;
    }
  }

  /**
   * Generates an image thumbnail from a File object
   */
  private async generateImageThumbnailFromFile(file: File): Promise<string | null> {
    try {
      // Create object URL for the file
      const objectUrl = URL.createObjectURL(file);
      
      return new Promise<string | null>((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          try {
            // Create canvas for thumbnail
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              URL.revokeObjectURL(objectUrl);
              resolve(null);
              return;
            }

            // Calculate thumbnail dimensions
            const maxSize = 200;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
            }

            canvas.width = width;
            canvas.height = height;

            // Draw image to canvas
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to data URL
            const thumbnail = canvas.toDataURL(MimeTypes.PNG);
            URL.revokeObjectURL(objectUrl);
            resolve(thumbnail);
          } catch (error) {
            this.logger.error(`Error creating image thumbnail: ${error}`);
            URL.revokeObjectURL(objectUrl);
            resolve(null);
          }
        };

        img.onerror = () => {
          this.logger.error(`Error loading image file for thumbnail: ${file.name}`);
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };

        img.src = objectUrl;
      });
    } catch (error) {
      this.logger.error(`Error generating image thumbnail from file: ${error}`);
      return null;
    }
  }

  /**
   * Generates a markdown thumbnail from a File object
   */
  private async generateMarkdownThumbnailFromFile(file: File): Promise<string | null> {
    try {
      // Read file content as text
      const markdownContent = await file.text();
      
      // Use the same method that's used for updates
      const thumbnail = Html2CanvasThumbnailHelper.createMarkdownThumbnailFromContent(
        markdownContent || `# ${file.name.replace(FileExtensions.MD, '')}\n\nNew markdown document`,
        file.name
      );
      
      return thumbnail || null;
    } catch (error) {
      this.logger.error(`Error generating markdown thumbnail from file: ${error}`);
      return null;
    }
  }

  /**
   * Generates a video thumbnail from a File object
   */
  private async generateVideoThumbnailFromFile(file: File): Promise<string | null> {
    try {
      this.logger.debug(`${LogMessages.PROCESS_GENERATING_VIDEO}: ${file.name}`);
      
      // Create object URL for the video file
      const objectUrl = URL.createObjectURL(file);
      
      return new Promise<string | null>((resolve) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true; // Required for autoplay
        video.style.display = 'none';
        
        video.onloadedmetadata = () => {
          try {
            // Set video to a specific time (e.g., 1 second or 10% of duration)
            const seekTime = Math.min(1, video.duration * 0.1);
            video.currentTime = seekTime;
          } catch (error) {
            this.logger.error(`Error setting video seek time: ${error}`);
            // If seek fails, try to capture at current position
            video.currentTime = 0;
          }
        };

        // Set up timeout fallback
        const timeout = setTimeout(() => {
          this.logger.warn(LogMessages.WARNING_TIMEOUT);
          try {
            // Try to capture thumbnail at current position if seek failed
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (ctx && video.videoWidth > 0) {
              const maxSize = 200;
              let width = video.videoWidth;
              let height = video.videoHeight;
              
              if (width > height) {
                if (width > maxSize) {
                  height = (height * maxSize) / width;
                  width = maxSize;
                }
              } else {
                if (height > maxSize) {
                  width = (width * maxSize) / height;
                  height = maxSize;
                }
              }

              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(video, 0, 0, width, height);
              
              const thumbnail = canvas.toDataURL(MimeTypes.PNG);
              URL.revokeObjectURL(objectUrl);
              clearTimeout(timeout);
              resolve(thumbnail);
            } else {
              URL.revokeObjectURL(objectUrl);
              clearTimeout(timeout);
              resolve(null);
            }
          } catch (error) {
            URL.revokeObjectURL(objectUrl);
            clearTimeout(timeout);
            resolve(null);
          }
        }, 10000); // 10 second timeout

        // Wrap resolve to clear timeout
        const resolveWithCleanup = (value: string | null) => {
          clearTimeout(timeout);
          resolve(value);
        };

        video.onseeked = () => {
          try {
            // Create canvas for thumbnail
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              URL.revokeObjectURL(objectUrl);
              resolveWithCleanup(null);
              return;
            }

            // Calculate thumbnail dimensions maintaining aspect ratio
            const maxSize = 200;
            const { videoWidth, videoHeight } = video;
            
            let width = videoWidth;
            let height = videoHeight;
            
            if (width > height) {
              if (width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
            }

            canvas.width = width;
            canvas.height = height;

            // Draw video frame to canvas
            ctx.drawImage(video, 0, 0, width, height);
            
            // Convert to data URL
            const thumbnail = canvas.toDataURL(MimeTypes.PNG);
            
            URL.revokeObjectURL(objectUrl);
            this.logger.info(LogMessages.SUCCESS_VIDEO_THUMBNAIL);
            resolveWithCleanup(thumbnail);
          } catch (error) {
            this.logger.error(`${LogMessages.ERROR_VIDEO_THUMBNAIL}: ${error}`);
            URL.revokeObjectURL(objectUrl);
            resolveWithCleanup(null);
          }
        };

        video.onerror = () => {
          this.logger.error(`${LogMessages.ERROR_VIDEO_THUMBNAIL}: ${file.name}`);
          URL.revokeObjectURL(objectUrl);
          resolveWithCleanup(null);
        };

        video.onloadstart = () => {
          this.logger.debug(LogMessages.PROCESS_VIDEO_LOADING);
        };

        // Load the video
        video.src = objectUrl;
        video.load();
      });
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_VIDEO_THUMBNAIL}: ${error}`);
      return null;
    }
  }

  /**
   * Generates a thumbnail for audio files (waveform visualization)
   */
  private async generateAudioThumbnailFromFile(file: File): Promise<string | null> {
    try {
      this.logger.debug(`${LogMessages.PROCESS_GENERATING_AUDIO}: ${file.name}`);
      
      // Create a visual representation for audio files
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        this.logger.error(LogMessages.ERROR_AUDIO_THUMBNAIL);
        return null;
      }

      // Set canvas size
      canvas.width = 200;
      canvas.height = 200;

      // Create a simple audio file icon/waveform visualization
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw audio icon background
      ctx.fillStyle = '#4a90e2';
      ctx.fillRect(20, 20, canvas.width - 40, canvas.height - 40);

      // Draw waveform-like pattern
      ctx.fillStyle = '#ffffff';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('🎵', canvas.width / 2, 50);
      
      // Draw file name (truncated if too long)
      const fileName = file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name;
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px Arial';
      ctx.fillText(fileName, canvas.width / 2, canvas.height - 30);
      
      // Draw some waveform bars
      ctx.fillStyle = '#ffffff';
      const barWidth = 4;
      const barSpacing = 2;
      const startX = 30;
      const barAreaWidth = canvas.width - 60;
      const numBars = Math.floor(barAreaWidth / (barWidth + barSpacing));
      
      for (let i = 0; i < numBars; i++) {
        const x = startX + i * (barWidth + barSpacing);
        const barHeight = Math.random() * 60 + 20; // Random height between 20 and 80
        const y = canvas.height / 2 - barHeight / 2;
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      // Convert to data URL
      const thumbnail = canvas.toDataURL(MimeTypes.PNG);
      this.logger.info(LogMessages.SUCCESS_AUDIO_THUMBNAIL);
      return thumbnail;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_AUDIO_THUMBNAIL}: ${error}`);
      return null;
    }
  }

  /**
   * Generates a generic thumbnail for document and archive files
   */
  private async generateGenericFileThumbnail(file: File): Promise<string | null> {
    try {
      this.logger.debug(`📄 Generating generic thumbnail for file: ${file.name}`);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        this.logger.error('Could not get canvas context for generic thumbnail');
        return null;
      }

      // Set canvas size
      canvas.width = 200;
      canvas.height = 200;

      // Get file extension for icon selection
      const fileName = file.name.toLowerCase();
      const fileType = fileName.substring(fileName.lastIndexOf('.'));
      
      // Background
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // File icon background based on type
      let backgroundColor = '#6c757d'; // default gray
      let icon = '📄'; // default document icon
      
      if ([FileExtensions.DOC, FileExtensions.DOCX].includes(fileType as FileExtensions)) {
        backgroundColor = '#2b579a'; // Word blue
        icon = '📝';
      } else if ([FileExtensions.PPT, FileExtensions.PPTX].includes(fileType as FileExtensions)) {
        backgroundColor = '#d24726'; // PowerPoint orange/red
        icon = '📊';
      } else if ([FileExtensions.TXT, FileExtensions.RTF].includes(fileType as FileExtensions)) {
        backgroundColor = '#28a745'; // Text green
        icon = '📃';
      } else if (FileExtensionGroups.ARCHIVES.includes(fileType as FileExtensions)) {
        backgroundColor = '#6f42c1'; // Archive purple
        icon = '🗜️';
      }

      // Draw file icon background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(30, 40, canvas.width - 60, canvas.height - 80);

      // Draw file type icon
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(icon, canvas.width / 2, canvas.height / 2 + 10);
      
      // Draw file extension
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px Arial';
      ctx.fillText(fileType.toUpperCase().substring(1), canvas.width / 2, canvas.height / 2 + 40);
      
      // Draw file name (truncated if too long)
      const displayName = file.name.length > 18 ? file.name.substring(0, 18) + '...' : file.name;
      ctx.fillStyle = '#343a40';
      ctx.font = '12px Arial';
      ctx.fillText(displayName, canvas.width / 2, canvas.height - 20);

      // Convert to data URL
      const thumbnail = canvas.toDataURL(MimeTypes.PNG);
      this.logger.info('✅ Generic file thumbnail generated successfully');
      return thumbnail;
    } catch (error) {
      this.logger.error(`Error generating generic file thumbnail: ${error}`);
      return null;
    }
  }

  /**
   * Generates a markdown thumbnail for a new file using the same method as updates
   */
  async generateMarkdownThumbnailForNewFile(markdownContent: string): Promise<string | null> {
    try {
      // Use the same method that's used for updates
      const thumbnail = Html2CanvasThumbnailHelper.createMarkdownThumbnailFromContent(
        markdownContent || '# New Document\n\nStart writing your content...',
        'new-document.md'
      );
      
      return thumbnail || null;
    } catch (error) {
      this.logger.error(`Error generating markdown thumbnail: ${error}`);
      return null;
    }
  }


  /**
   * Excel/CSV client-side thumbnail generation (Collabora-style preview).
   * Returns a ThumbnailResult with dataUrl, size and optional error.
   */
  async generateThumbnail(
    file: File,
    options: ThumbnailOptions = {}
  ): Promise<ThumbnailResult> {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: ${options.width || 380}px;
      height: ${options.height || 320}px;
      z-index: -1;
    `;
    document.body.appendChild(tempContainer);

    const elementRef = { nativeElement: tempContainer };

    try {
      switch (ext) {
        case '.xls':
        case '.xlsx':
        case '.csv':
          return await this.generateExcelThumbnailClientSide(file, elementRef, options);

        default:
          return {
            dataUrl: '',
            fileSize: 0,
            method: 'unsupported',
            error: `File type ${ext} is not supported for thumbnail generation`
          };
      }
    } finally {
      if (tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
    }
  }

  private async generateExcelThumbnailClientSide(
    file: File,
    previewElement: ElementRef,
    options: ThumbnailOptions = {}
  ): Promise<ThumbnailResult> {
    const defaultOptions = {
      width: 380,
      height: 320,
      quality: 0.8,
      format: 'png' as const
    };

    const opts = { ...defaultOptions, ...options };

    try {
      const container = previewElement.nativeElement;
      this.setupContainerForExcel(container);

      const data = await this.readExcelFileData(file);

      if (!data || data.length === 0) {
        this.createExcelErrorPreview(file, previewElement, 'No data found in file');
      } else {
        this.renderExcelTable(data, previewElement, file.name);
      }

      const dataUrl = await domtoimage.toPng(container, {
        width: opts.width,
        height: opts.height,
        quality: opts.quality,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left'
        }
      });

      return {
        dataUrl,
        fileSize: dataUrl.length,
        method: 'client-side-excel'
      };
    } catch (error) {
      this.logger.error(`Error generating Excel thumbnail: ${error}`);
      this.createExcelErrorPreview(file, previewElement, error instanceof Error ? error.message : 'Unknown error');

      const dataUrl = await domtoimage.toPng(previewElement.nativeElement, {
        width: opts.width,
        height: opts.height,
        quality: opts.quality
      });

      return {
        dataUrl,
        fileSize: dataUrl.length,
        method: 'client-side-excel',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private async readExcelFileData(file: File): Promise<any[][]> {
    try {
      const fileName = file.name.toLowerCase();
      const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      const isCSV = fileName.endsWith('.csv');

      if (!isExcel && !isCSV) {
        throw new Error(`Unsupported file format. Only .xlsx, .xls and .csv files are supported.`);
      }

      this.logger.debug(`📊 Reading file: ${fileName} (${file.size} bytes)`);

      if (isCSV) {
        return await this.parseCSVFile(file);
      } else {
        return await this.parseExcelFile(file);
      }
    } catch (error) {
      this.logger.error(`Error reading file: ${error}`);
      throw error;
    }
  }

  private async parseCSVFile(file: File): Promise<any[][]> {
    const text = await file.text();
    const lines = text.split('\n').slice(0, 20);

    const rows = lines.map(line => {
      const cells = line.split(',').map(cell =>
        cell.trim().replace(/^"(.*)"$/, '$1')
      );
      return cells.slice(0, 10);
    }).filter(row => row.some(cell => cell.trim()));

    this.logger.debug(`✅ Successfully parsed ${rows.length} rows from CSV file`);
    return rows;
  }

  private async parseExcelFile(file: File): Promise<any[][]> {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    let textContent = '';
    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i];
      if ((byte >= 32 && byte <= 126) || byte >= 160) {
        textContent += String.fromCharCode(byte);
      } else if (byte === 9 || byte === 10 || byte === 13) {
        textContent += String.fromCharCode(byte);
      }
    }

    const extractedData = this.extractDataPatterns(textContent);

    if (extractedData.length === 0) {
      return this.generateSampleData(file.name);
    }

    this.logger.debug(`✅ Successfully extracted ${extractedData.length} rows from Excel file`);
    return extractedData;
  }

  private extractDataPatterns(text: string): any[][] {
    const rows: any[][] = [];
    const lines = text.split(/[\n\r]+/).slice(0, 20);

    for (const line of lines) {
      if (line.trim().length < 2) continue;

      let cells: string[] = [];
      const patterns = line.match(/[A-Za-z0-9][A-Za-z0-9\s\-\.\/,]*[A-Za-z0-9]|[A-Za-z0-9]/g);

      if (patterns && patterns.length > 0) {
        cells = patterns
          .slice(0, 10)
          .map(cell => cell.trim())
          .filter(cell => cell.length > 0 && cell.length < 50);
      }

      if (cells.length > 0) {
        rows.push(cells);
      }

      if (rows.length >= 20) break;
    }

    return rows;
  }

  private generateSampleData(_fileName: string): any[][] {
    return [
      ['Name', 'Email', 'Department', 'Status', 'Date', 'Amount'],
      ['John Smith', 'john.smith@company.com', 'Sales', 'Active', '2025-12-01', '$2,500'],
      ['Sarah Johnson', 'sarah.j@company.com', 'Marketing', 'Active', '2025-12-02', '$3,200'],
      ['Mike Chen', 'mike.chen@company.com', 'Engineering', 'Pending', '2025-12-03', '$4,100'],
      ['Emily Davis', 'emily.d@company.com', 'HR', 'Active', '2025-12-04', '$2,800'],
      ['Alex Rodriguez', 'alex.r@company.com', 'Finance', 'Review', '2025-12-05', '$5,600'],
      ['Lisa Wang', 'lisa.wang@company.com', 'Sales', 'Active', '2025-12-06', '$3,900'],
      ['David Brown', 'david.b@company.com', 'Support', 'Active', '2025-12-07', '$2,300']
    ];
  }

  private setupContainerForExcel(container: HTMLElement): void {
    container.style.cssText = `
      width: 380px;
      height: 320px;
      background: #ffffff;
      border: 1px solid #dadce0;
      border-radius: 8px;
      overflow: hidden;
      font-family: 'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      position: relative;
      box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3);
    `;
    container.innerHTML = '';
  }

  private renderExcelTable(data: any[][], previewElement: ElementRef, fileName?: string): void {
    const container = previewElement.nativeElement;

    const toolbar = document.createElement('div');
    toolbar.style.cssText = `
      background: #f5f5f5;
      border-bottom: 1px solid #d9d9d9;
      padding: 8px 12px;
      font-size: 12px;
      color: #333;
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 36px;
    `;

    toolbar.innerHTML = `
      <div style="display: flex; align-items: center; gap: 4px;">
        <div style="width: 16px; height: 16px; background: #0078d4; border-radius: 2px; display: flex; align-items: center; justify-content: center;">
          <span style="color: white; font-size: 10px; font-weight: bold;">C</span>
        </div>
        <span style="font-weight: 500; color: #333;">${fileName?.replace(/\.(xlsx?|csv)$/i, '') || 'Spreadsheet'}</span>
      </div>
      <div style="flex: 1;"></div>
      <div style="display: flex; align-items: center; gap: 6px; font-size: 11px; color: #666;">
        <span>Saved</span>
        <div style="width: 8px; height: 8px; background: #28a745; border-radius: 50%;"></div>
      </div>
    `;
    container.appendChild(toolbar);

    const documentArea = document.createElement('div');
    documentArea.style.cssText = `
      height: calc(320px - 37px);
      background: #f8f9fa;
      padding: 16px;
      overflow: hidden;
      position: relative;
    `;

    const spreadsheetContainer = document.createElement('div');
    spreadsheetContainer.style.cssText = `
      background: white;
      border: 1px solid #ddd;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      overflow: hidden;
      height: 100%;
      position: relative;
    `;

    const columnHeaders = document.createElement('div');
    columnHeaders.style.cssText = `
      display: flex;
      background: #f1f3f4;
      border-bottom: 1px solid #dadce0;
      height: 28px;
    `;

    const rowNumHeader = document.createElement('div');
    rowNumHeader.style.cssText = `
      width: 40px;
      border-right: 1px solid #dadce0;
      background: #f1f3f4;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      color: #5f6368;
      font-weight: 500;
    `;
    columnHeaders.appendChild(rowNumHeader);

    const maxCols = Math.min(data[0]?.length || 0, 6);
    for (let i = 0; i < maxCols; i++) {
      const colHeader = document.createElement('div');
      colHeader.textContent = String.fromCharCode(65 + i);
      colHeader.style.cssText = `
        min-width: 100px;
        border-right: 1px solid #dadce0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 500;
        color: #5f6368;
        background: #f1f3f4;
      `;
      columnHeaders.appendChild(colHeader);
    }

    spreadsheetContainer.appendChild(columnHeaders);

    const dataRows = document.createElement('div');
    dataRows.style.cssText = `
      height: calc(100% - 28px);
      overflow: hidden;
    `;

    const displayData = data.slice(0, 8);
    displayData.forEach((row, rowIndex) => {
      const rowElement = document.createElement('div');
      rowElement.style.cssText = `
        display: flex;
        height: 32px;
        border-bottom: 1px solid #e8eaed;
        background: ${rowIndex % 2 === 0 ? '#ffffff' : '#fafbfc'};
      `;

      const rowNumber = document.createElement('div');
      rowNumber.textContent = String(rowIndex + 1);
      rowNumber.style.cssText = `
        width: 40px;
        border-right: 1px solid #dadce0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        color: #5f6368;
        background: #f8f9fa;
        font-weight: 500;
      `;
      rowElement.appendChild(rowNumber);

      for (let colIndex = 0; colIndex < maxCols; colIndex++) {
        const cell = document.createElement('div');
        const cellValue = row[colIndex] || '';
        cell.textContent = String(cellValue).substring(0, 15);

        let cellBg = rowIndex % 2 === 0 ? '#ffffff' : '#fafbfc';
        let textColor = '#202124';
        let fontWeight = '400';

        if (rowIndex === 0) {
          cellBg = '#e3f2fd';
          textColor = '#1565c0';
          fontWeight = '600';
        }

        cell.style.cssText = `
          min-width: 100px;
          border-right: 1px solid #e8eaed;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          font-size: 12px;
          color: ${textColor};
          background: ${cellBg};
          font-weight: ${fontWeight};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `;
        rowElement.appendChild(cell);
      }

      dataRows.appendChild(rowElement);
    });

    spreadsheetContainer.appendChild(dataRows);
    documentArea.appendChild(spreadsheetContainer);
    container.appendChild(documentArea);
  }

  private createExcelErrorPreview(file: File, previewElement: ElementRef, errorMessage?: string): void {
    const container = previewElement.nativeElement;
    container.innerHTML = '';

    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      width: 380px;
      height: 320px;
      background: #ffffff;
      border: 1px solid #dadce0;
      border-radius: 8px;
      padding: 32px 24px;
      font-family: 'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      box-sizing: border-box;
    `;

    errorDiv.innerHTML = `
      <div style="width: 48px; height: 48px; background: #ea4335; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 24px;">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      </div>
      <div style="font-size: 16px; font-weight: 400; color: #3c4043; margin-bottom: 8px;">
        Can't preview file
      </div>
      <div style="font-size: 14px; color: #5f6368; margin-bottom: 16px;">
        ${file.name} • ${this.formatFileSize(file.size)}
      </div>
      <div style="font-size: 14px; color: #5f6368; line-height: 1.4; max-width: 280px;">
        ${errorMessage || 'This file format cannot be previewed. You can still upload and view it in the appropriate application.'}
      </div>
    `;

    container.appendChild(errorDiv);
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}